import { useState, useMemo, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useTheme } from "@/context/ThemeContext";
import { useBrand } from "@/context/BrandContext";
import defaultLogo from "@/assets/skoda-logo.png";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { KeycloakRealmBanner } from "@/components/KeycloakRealmBanner";
import { ImpersonationSearch } from "@/components/ImpersonationSearch";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { PERM, hasPermissionAnywhere, isMhqUser } from "@/lib/permissions";
import {
  Home,
  Store,
  FileText,
  LayoutGrid,
  Users,
  Settings as SettingsIcon,
  Sun,
  Moon,
  Monitor,
  User as UserIcon,
  LogOut,
  Menu as MenuIcon,
  ScrollText,
  Zap,
  Radio,
  Globe,
  Archive,
  Building2,
} from "lucide-react";

function buildNav(user, impersonating, t) {
  // Every item gates on a permission (see lib/permissions.js); which role
  // grants it is the backend's business. `can` is deliberately the
  // "anywhere" form — a nav item isn't about one dealer yet.
  const can = (permission) => hasPermissionAnywhere(user, permission);
  const isMhq = isMhqUser(user);
  const acting = !!impersonating;
  // An impersonating MHQ user is always looking at exactly one dealer (the
  // impersonated one) — the multi-dealer list only applies to an actual
  // Dealer-scoped user with more than one grant of their own.
  const dealerCount = user.dealer_access?.length || 0;

  // If MHQ user is impersonating a dealer, show DEALER-side menu
  if (acting || !isMhq) {
    const items = [{ to: "/", label: t("nav.home"), icon: Home, testId: "nav-home" }];
    if (acting || dealerCount <= 1) {
      items.push({ to: "/my-dealership", label: t("nav.myDealership"), icon: Store, testId: "nav-my-dealership" });
    } else {
      items.push({ to: "/my-dealerships", label: t("nav.myDealerships"), icon: Store, testId: "nav-my-dealerships" });
    }
    if (can(PERM.LEGAL_PAGES_EDIT)) {
      items.push({ to: "/legal-pages", label: t("nav.legalPages"), icon: FileText, testId: "nav-legal-pages" });
    }
    // No dealer-side DNS self-service any more — dealer.dns.manage is only
    // ever granted network-wide, so this stays out of the dealer nav.
    if (can(PERM.USERS_MANAGE)) {
      items.push({ to: "/users", label: t("nav.users"), icon: Users, testId: "nav-users" });
    }
    return items;
  }

  const items = [{ to: "/", label: t("nav.home"), icon: Home, testId: "nav-home" }];
  if (can(PERM.NETWORK_DEALERS_READ)) {
    items.push({ to: "/dealers", label: t("nav.dealers"), icon: LayoutGrid, testId: "nav-dealers" });
  }
  if (can(PERM.ACTIVATIONS_MANAGE)) {
    items.push({ to: "/activations", label: "Activations", icon: Zap, testId: "nav-activations" });
  }
  if (can(PERM.DNS_MANAGE)) {
    items.push({ to: "/dns-setup", label: "DNS Setup", icon: Globe, testId: "nav-dns-setup" });
  }
  if (can(PERM.USERS_MANAGE)) {
    items.push({ to: "/users", label: t("nav.users"), icon: Users, testId: "nav-users" });
  }
  if (can(PERM.IMPORTS_RUN)) {
    items.push({ to: "/imports", label: "Imports", icon: Archive, testId: "nav-imports" });
  }
  if (can(PERM.LEGAL_TEMPLATES_MANAGE)) {
    items.push({ to: "/legal-html-editor", label: t("nav.legalHtmlEditor"), icon: FileText, testId: "nav-legal-html-editor" });
  }
  if (can(PERM.DEPARTMENTS_MANAGE)) {
    items.push({ to: "/departments", label: t("nav.departments"), icon: Building2, testId: "nav-departments" });
  }
  if (can(PERM.SYSTEM_SETTINGS_MANAGE)) {
    items.push({ to: "/settings", label: t("nav.settings"), icon: SettingsIcon, testId: "nav-settings" });
  }
  if (can(PERM.SYSTEM_AUDIT_READ)) {
    items.push({ to: "/audit-log", label: t("nav.audit"), icon: ScrollText, testId: "nav-audit" });
    items.push({ to: "/api-logs", label: "API Logs", icon: Radio, testId: "nav-api-logs" });
  }
  return items;
}

export function Shell({ children }) {
  const { user, impersonating, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { theme, setTheme } = useTheme();
  const { brand } = useBrand();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const nav = useMemo(() => buildNav(user, impersonating, t), [user, impersonating, t]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const canImpersonate = hasPermissionAnywhere(user, PERM.NETWORK_IMPERSONATE);

  const SidebarInner = (
    <div className="flex h-full w-64 flex-col border-r border-border bg-secondary/30">
      <div className="flex flex-col items-start gap-1.5 border-b border-border px-4 py-3">
        <img
          src={brand.brand_logo_url || defaultLogo}
          alt={brand.brand_name}
          className="h-8 w-auto shrink-0 rounded-sm object-contain"
        />
        <span className="text-sm font-semibold leading-tight" data-testid="sidebar-brand-name">
          {brand.brand_name}
        </span>
      </div>
      <nav className="flex-1 space-y-1 p-3" data-testid="sidebar-nav">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            data-testid={item.testId}
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
          >
            <item.icon className="h-4 w-4" strokeWidth={1.75} />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-3">
        <div className="rounded-md border border-dashed border-border p-3">
          <p className="data-label">{t("common.signedInAs")}</p>
          <p className="mt-1 text-sm font-medium" data-testid="sidebar-user-name">
            {user?.name}
          </p>
          <p className="text-xs text-muted-foreground" data-testid="sidebar-user-email">
            {user?.email}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen w-full flex-col bg-background">
      <ImpersonationBanner />
      <KeycloakRealmBanner />

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop sidebar */}
        <aside className="hidden md:block" data-testid="sidebar-desktop">
          {SidebarInner}
        </aside>

        {/* Main column */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Top bar */}
          <header
            data-testid="top-bar"
            className="sticky top-0 z-30 flex h-14 items-center justify-between gap-2 border-b border-border bg-background/95 px-3 backdrop-blur md:px-6"
          >
            <div className="flex items-center gap-2">
              {/* Mobile menu button */}
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button
                    data-testid="mobile-menu-btn"
                    variant="ghost"
                    size="icon"
                    className="md:hidden"
                    aria-label="Open menu"
                  >
                    <MenuIcon className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-64 p-0">
                  {SidebarInner}
                </SheetContent>
              </Sheet>
              {canImpersonate && !impersonating && (
                <div className="hidden md:block">
                  <ImpersonationSearch />
                </div>
              )}
            </div>

            <div className="flex items-center gap-1.5">
              {/* Language switch */}
              <div
                className="flex items-center rounded-md border border-border p-0.5 font-mono text-[11px]"
                data-testid="lang-switch"
              >
                {["en", "de"].map((l) => (
                  <button
                    key={l}
                    data-testid={`lang-btn-${l}`}
                    onClick={() => setLang(l)}
                    className={`rounded px-2 py-1 uppercase tracking-widest transition-colors ${
                      lang === l ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>

              {/* Theme switch */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="theme-btn" variant="ghost" size="icon" aria-label="Theme">
                    {theme === "dark" ? <Moon className="h-4 w-4" /> : theme === "light" ? <Sun className="h-4 w-4" /> : <Monitor className="h-4 w-4" />}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-36">
                  <DropdownMenuItem data-testid="theme-light" onClick={() => setTheme("light")}>
                    <Sun className="mr-2 h-4 w-4" /> {t("profile.themeLight")}
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="theme-dark" onClick={() => setTheme("dark")}>
                    <Moon className="mr-2 h-4 w-4" /> {t("profile.themeDark")}
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="theme-system" onClick={() => setTheme("system")}>
                    <Monitor className="mr-2 h-4 w-4" /> {t("profile.themeSystem")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Profile dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="profile-btn" variant="ghost" className="gap-2 pl-2 pr-3">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                      {(user?.name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <span className="hidden text-sm font-medium sm:inline">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    {user?.email}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="profile-menu-profile" onClick={() => navigate("/profile")}>
                    <UserIcon className="mr-2 h-4 w-4" />
                    {t("nav.profile")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="profile-menu-logout" onClick={logout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    {t("nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto" data-testid="main-content">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
